import { useCallback, useEffect } from "react"

import { useMixpanel } from "@src/providers/MixpanelProvider"
import { hashForAnalytics } from "@src/utils/analyticsSanitize"
import { trackEvent } from "@src/utils/trackEvent"
import { useAnalyticsSuppressed } from "./useAnalyticsSuppressed"

export const useSignInLogger = (
  address: string | undefined,
  chainType: string | undefined,
  isAuthorized: boolean
) => {
  const mixPanel = useMixpanel()
  const suppressed = useAnalyticsSuppressed()
  const storageKey = "signedInAddress"

  const sendTrackingEvent = useCallback(
    async (eventName: string) => {
      if (suppressed) return
      const hashedAddress = await hashForAnalytics(address)
      const payload = {
        chain: chainType,
        wallet_address_hash: hashedAddress,
        timestamp: Date.now(),
      }
      mixPanel?.track(eventName, payload)
      window.gtag?.("event", eventName, payload)
    },
    [chainType, address, mixPanel, suppressed]
  )

  useEffect(() => {
    if (address != null && isAuthorized) {
      try {
        if (!localStorage.getItem(storageKey)) {
          localStorage.setItem(storageKey, address)
          void sendTrackingEvent("wallet_connection_success").catch(() => {})
        }
        const accountSeenKey = `defuse_account_seen:${address}`
        if (!localStorage.getItem(accountSeenKey)) {
          localStorage.setItem(accountSeenKey, "1")
          trackEvent("account_created", { auth_method: chainType })
        }
      } catch {
        // localStorage may be unavailable
      }
    }
  }, [address, chainType, isAuthorized, sendTrackingEvent])

  return {
    onSignOut: useCallback(() => {
      try {
        localStorage.removeItem(storageKey)
      } catch {
        // localStorage may be unavailable
      }
    }, []),
  }
}
